/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { I18nMessage, useTranslations } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { ThemeType } from "@packages/ui";
import {
  Button,
  Calendar,
  createModal,
  cssMediaQueryMinWidthTablet,
  numericPixels,
  radius,
  spacing,
  StickyFooterModal,
  stroke,
  Text,
  useModal,
} from "@packages/ui";
import { isSameDay, startOfDay } from "date-fns";
import { useCallback, useMemo, useState } from "react";

import { allQuarterHourTimesInOrder } from "../../../../../components/parishAdminDashboard/events/form/quarterHourTimes";
import { TimeQuarterHourList } from "../../../../../components/parishAdminDashboard/events/form/TimeQuarterHourList";
import type { TimeValue } from "../../../../../lib/utils/hallowAdminDashboard/eventForm.model";
import {
  buildLocalDate,
  timeToMinutes,
} from "../../../../../lib/utils/hallowAdminDashboard/events";

/** The design's dialog: calendar and time column side by side above tablet. */
const cssModal: Interpolation = {
  [cssMediaQueryMinWidthTablet]: {
    maxWidth: "668px",
    width: "668px",
  },
};

const cssPickerRow: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
  [cssMediaQueryMinWidthTablet]: {
    alignItems: "stretch",
    flexDirection: "row",
  },
};

/**
 * Mirrors the events date/time modal: the calendar takes whatever the fixed
 * time column leaves, and `flex-basis: 0` keeps `Calendar`'s max-content width
 * (a month strip, not the day grid) from deciding the split.
 */
const cssCalendar: Interpolation<ThemeType> = (theme) => ({
  border: `${stroke.regular} solid ${theme.semanticColors.neutralsLowest}`,
  borderRadius: radius.l,
  boxSizing: "border-box",
  padding: `${spacing.s} ${spacing.none}`,
  width: "100%",
  [cssMediaQueryMinWidthTablet]: {
    flex: "1 1 0",
    paddingInline: spacing.l,
    width: "auto",
  },
});

/** The design's 164px time row plus its 8px of padding on each side. */
const TIME_COLUMN_WIDTH = 180;

/**
 * Stacked, the list gets a fixed height of its own. Side by side, `contain: size`
 * makes this box's intrinsic height 0 whatever its 96 quarter-hour rows would
 * ask for, so the row is sized by the calendar and `align-items: stretch` hands
 * that height back here.
 */
const cssTimeList: Interpolation = {
  height: numericPixels[360],
  minHeight: 0,
  width: "100%",
  [cssMediaQueryMinWidthTablet]: {
    contain: "size",
    flex: `0 0 ${TIME_COLUMN_WIDTH}px`,
    height: "auto",
    width: "auto",
  },
};

const cssDescription: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedium,
  display: "block",
  paddingBottom: spacing.m,
});

const cssFooter: Interpolation = {
  display: "flex",
  gap: spacing.s,
  justifyContent: "flex-end",
};

/** 12:00 AM through 11:45 PM, in order. Fixed, so it is built once. */
const TIME_OPTIONS = allQuarterHourTimesInOrder();

const toTimeValue = (date: Date): TimeValue => {
  const hour24 = date.getHours();
  return {
    hour: hour24 % 12 === 0 ? 12 : hour24 % 12,
    minute: date.getMinutes(),
    amPm: hour24 < 12 ? "AM" : "PM",
  };
};

/**
 * The first quarter hour strictly after `from`, so the default selection is
 * always a time the admin can still schedule. Rolls into the next day near
 * midnight, which is what puts today out of the calendar's reach below.
 */
const nextQuarterHour = (from: Date): Date => {
  const next = new Date(from);
  next.setSeconds(0, 0);
  next.setMinutes(next.getMinutes() + (15 - (next.getMinutes() % 15)));
  return next;
};

export type BroadcastScheduleModalResult = {
  /** The chosen send time, or `null` when the admin cleared the schedule. */
  scheduledAt: Date | null;
};

export type BroadcastScheduleModalProps = {
  communityId: number;
  /** The schedule already on the draft, if any. */
  scheduledAt?: Date | null;
};

/**
 * "Schedule Broadcast" — a calendar beside the day's quarter-hour times, per
 * Scenarios 3a and 3b of COM-209. Confirm resolves the chosen instant; Clear
 * resolves `null`; dismissing resolves nothing and leaves the draft alone.
 *
 * Reuses `TimeQuarterHourList` from the events form: the two pickers are the
 * same control, and it already implements the listbox keyboard pattern.
 */
export const BroadcastScheduleModal = createModal<BroadcastScheduleModalProps>(
  ({ communityId, scheduledAt }: BroadcastScheduleModalProps) => {
    const t = useTranslations();
    const scheduleModal = useModal(BroadcastScheduleModal);
    const { track } = useAnalytics();

    /**
     * Adding a first schedule offers Cancel; changing one that already exists
     * offers Clear, and the header's close button is what backs out of that
     * case instead.
     */
    const isModifying = Boolean(scheduledAt);

    /**
     * Pinned to when the modal opened rather than recomputed each render, so
     * the selection cannot be invalidated underneath the admin mid-edit.
     */
    const earliest = useMemo(() => nextQuarterHour(new Date()), []);
    const earliestDay = useMemo(() => startOfDay(earliest), [earliest]);
    const earliestMinutes = useMemo(
      () => timeToMinutes(toTimeValue(earliest)),
      [earliest],
    );

    const [date, setDate] = useState<Date>(() =>
      scheduledAt ? startOfDay(scheduledAt) : earliestDay,
    );
    const [time, setTime] = useState<TimeValue>(() =>
      scheduledAt ? toTimeValue(scheduledAt) : toTimeValue(earliest),
    );

    /** Past days are unreachable; so is today once its last slot has gone. */
    const disabledDays = useMemo(
      () => ({ before: earliestDay }),
      [earliestDay],
    );

    const isTimeSelectableOn = useCallback(
      (day: Date, candidate: TimeValue) =>
        !isSameDay(day, earliestDay) ||
        timeToMinutes(candidate) >= earliestMinutes,
      [earliestDay, earliestMinutes],
    );

    const isTimeSelectable = useCallback(
      (candidate: TimeValue) => isTimeSelectableOn(date, candidate),
      [date, isTimeSelectableOn],
    );

    /**
     * Moving to today can put the selection out of reach. Leaving it
     * selected-but-greyed would let Confirm schedule a send in the past, so
     * bump it to the earliest slot the new day accepts.
     */
    const handleCalendarSelect = useCallback(
      (selected: Date | undefined) => {
        if (!selected) return;
        setDate(selected);

        if (!isTimeSelectableOn(selected, time)) {
          const bumped = TIME_OPTIONS.find((option) =>
            isTimeSelectableOn(selected, option),
          );
          // Absent only on a day with no reachable slot, which `disabledDays`
          // keeps out of the calendar.
          if (bumped) setTime(bumped);
        }
      },
      [isTimeSelectableOn, time],
    );

    const handleConfirm = useCallback(() => {
      track({
        event: HallowAnalyticsEvent.TappedConfirmBroadcastSchedule,
        properties: { community_id: communityId },
      });
      scheduleModal.resolve({ scheduledAt: buildLocalDate(date, time) });
      scheduleModal.remove();
    }, [communityId, date, scheduleModal, time, track]);

    /**
     * Clearing an existing schedule. Resolving `null` is what the caller reads
     * as "no longer scheduled" — on an already-created broadcast that is what
     * sends it now, which is why this is only offered once there is a schedule
     * to remove.
     */
    const handleClear = useCallback(() => {
      scheduleModal.resolve({ scheduledAt: null });
      scheduleModal.remove();
    }, [scheduleModal]);

    /**
     * Backing out of a first schedule. Resolves nothing, so the caller keeps
     * whatever it had, exactly as dismissing the modal does.
     */
    const handleCancel = useCallback(() => {
      scheduleModal.remove();
    }, [scheduleModal]);

    const handleOpenChange = useCallback(
      (open: boolean) => {
        if (!open) scheduleModal.remove();
      },
      [scheduleModal],
    );

    return (
      <StickyFooterModal
        open={scheduleModal.visible}
        onOpenChange={handleOpenChange}
        title={t("dashboard_broadcast_schedule_title")}
        modalProps={{ contentCSS: cssModal }}
        footerChildren={
          <div css={cssFooter}>
            <Button
              size="l"
              variant="secondary"
              type="button"
              onClick={isModifying ? handleClear : handleCancel}
            >
              {isModifying ? t("web_words_clear") : t("general_word_cancel")}
            </Button>
            <Button
              size="l"
              variant="primary"
              type="button"
              onClick={handleConfirm}
            >
              {t("general_word_confirm")}
            </Button>
          </div>
        }
      >
        <Text size="m" type="body" css={cssDescription}>
          <I18nMessage message="dashboard_broadcast_schedule_description" />
        </Text>
        <div css={cssPickerRow}>
          <Calendar
            mode="single"
            monthNavigation="caption"
            selected={date}
            onSelect={handleCalendarSelect}
            defaultMonth={date}
            disabled={disabledDays}
            css={cssCalendar}
          />
          <div css={cssTimeList}>
            <TimeQuarterHourList
              label={t("events_date_time_select_time")}
              selectedTime={time}
              onSelect={setTime}
              isTimeSelectable={isTimeSelectable}
              recenterKey={date.toDateString()}
            />
          </div>
        </div>
      </StickyFooterModal>
    );
  },
);
